import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

/**
 * VerifyPage requires an explicit user click to consume the magic link token.
 *
 * Why: Many enterprise email systems (Microsoft Defender SafeLinks, Proofpoint
 * URL Defense, Mimecast, Barracuda, etc.) pre-fetch links in incoming emails
 * using a headless browser that executes JavaScript. If we auto-verify on page
 * mount, the scanner consumes the single-use token before the recipient can
 * click it, and the user sees "Invalid or Expired" on their first try.
 *
 * Requiring a button click defeats automated scanners (they load the page but
 * don't simulate clicks) while remaining a one-tap experience for the user.
 */
const VerifyPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyMagicLink, isAuthenticated } = useAuth();
  const [status, setStatus] = useState('confirm'); // 'confirm' | 'verifying' | 'success' | 'error'
  const [message, setMessage] = useState('');
  const hasProcessed = useRef(false);

  const token = useMemo(() => {
    let raw = searchParams.get('token');
    if (!raw) return '';
    raw = raw.trim();
    try {
      const decoded = decodeURIComponent(raw);
      if (decoded !== raw && !decoded.includes('%')) {
        raw = decoded;
      }
    } catch (e) {
      console.warn('Token decode warning:', e);
    }
    return raw;
  }, [searchParams]);

  useEffect(() => {
    if (isAuthenticated) {
      console.log('VerifyPage: Already authenticated, redirecting to dashboard');
      navigate('/dashboard', { replace: true });
      return;
    }

    if (!token) {
      setStatus('error');
      setMessage('No verification token provided. Please request a new login link.');
      return;
    }

    const verificationKey = `magic_link_verified_${token}`;
    const storedData = sessionStorage.getItem(verificationKey);
    if (storedData) {
      try {
        const { status: storedStatus, timestamp } = JSON.parse(storedData);
        const now = Date.now();
        const fiveMinutes = 5 * 60 * 1000;
        if (storedStatus === 'completed' && now - timestamp < fiveMinutes) {
          setStatus('success');
          setMessage('You are already signed in. Redirecting to your dashboard...');
          setTimeout(() => navigate('/dashboard', { replace: true }), 800);
          return;
        }
        if (now - timestamp >= fiveMinutes) {
          sessionStorage.removeItem(verificationKey);
        }
      } catch (e) {
        sessionStorage.removeItem(verificationKey);
      }
    }
  }, [token, isAuthenticated, navigate]);

  const handleVerify = async () => {
    if (hasProcessed.current || !token) return;
    hasProcessed.current = true;

    const verificationKey = `magic_link_verified_${token}`;
    setStatus('verifying');
    setMessage('');

    try {
      sessionStorage.setItem(
        verificationKey,
        JSON.stringify({ status: 'processing', timestamp: Date.now() })
      );

      localStorage.removeItem('customerToken');
      localStorage.removeItem('customerRefreshToken');
      localStorage.removeItem('customerData');
      localStorage.removeItem('customerCurrentRoute');

      toast.dismiss();

      const result = await verifyMagicLink(token);

      if (result.success) {
        setStatus('success');
        setMessage('Login successful! Redirecting to your dashboard...');
        sessionStorage.setItem(
          verificationKey,
          JSON.stringify({ status: 'completed', timestamp: Date.now() })
        );
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 1200);
      } else {
        sessionStorage.setItem(
          verificationKey,
          JSON.stringify({ status: 'failed', timestamp: Date.now() })
        );
        setStatus('error');
        setMessage(result.message || 'Invalid or expired login link');
        hasProcessed.current = false;
        toast.dismiss();
        setTimeout(() => {
          toast.error(result.message || 'Invalid or expired login link', {
            id: 'magic-link-error',
            duration: 5000
          });
        }, 100);
      }
    } catch (error) {
      console.error('VerifyPage: Verification error:', error);
      sessionStorage.setItem(
        verificationKey,
        JSON.stringify({ status: 'error', timestamp: Date.now() })
      );
      setStatus('error');
      setMessage('Failed to verify login link. Please try again.');
      hasProcessed.current = false;
      toast.dismiss();
      setTimeout(() => {
        toast.error('Failed to verify login link. Please try again.', {
          id: 'magic-link-error',
          duration: 5000
        });
      }, 100);
    }
  };

  const getIcon = () => {
    switch (status) {
      case 'verifying':
        return <ArrowPathIcon className="w-12 h-12 text-blue-600 animate-spin" />;
      case 'success':
        return <CheckCircleIcon className="w-12 h-12 text-green-600" />;
      case 'error':
        return <XCircleIcon className="w-12 h-12 text-red-600" />;
      case 'confirm':
      default:
        return <LockClosedIcon className="w-12 h-12 text-blue-600" />;
    }
  };

  const getTitle = () => {
    switch (status) {
      case 'verifying':
        return 'Signing you in...';
      case 'success':
        return 'Login Successful!';
      case 'error':
        return 'Verification Failed';
      case 'confirm':
      default:
        return 'Confirm Your Sign-In';
    }
  };

  const getBackgroundColor = () => {
    switch (status) {
      case 'success':
        return 'bg-green-100';
      case 'error':
        return 'bg-red-100';
      default:
        return 'bg-blue-100';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-xl p-8 text-center"
        >
          <div
            className={`mx-auto w-20 h-20 ${getBackgroundColor()} rounded-full flex items-center justify-center mb-6`}
          >
            {getIcon()}
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-4">{getTitle()}</h1>

          <p className="text-gray-600 mb-6">
            {status === 'confirm' && !message
              ? 'Click the button below to securely sign in to your account.'
              : message}
          </p>

          {status === 'confirm' && token && (
            <div className="space-y-4">
              <button
                onClick={handleVerify}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors flex items-center justify-center gap-2"
              >
                Continue to Sign In
                <ArrowRightIcon className="w-5 h-5" />
              </button>
              <p className="text-xs text-gray-500">
                For your security, we require one extra tap to confirm it's really you
                signing in.
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-4">
              <button
                onClick={() => navigate('/auth/login', { replace: true })}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                Request New Login Link
              </button>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>Common issues:</strong>
                  <br />• The link may have expired (links are valid for 30 minutes)
                  <br />• You may have already used this link
                  <br />• The link may have been corrupted when copied
                </p>
              </div>
            </div>
          )}

          {status === 'success' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-800">
                Welcome back! You'll be redirected to your dashboard shortly.
              </p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default VerifyPage;
